import { ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NodeWithoutDescription } from '../../../api/table-of-content';

import List from '@pelckmans/elementis/components/list';

type Level = NonNullable<ComponentProps<typeof List.Item>['level']>;

type Props = {
  node: NodeWithoutDescription;
  level: number;
  renderBranches: (nodes: NodeWithoutDescription[], level?: number, hasCollapsibleParent?: boolean) => JSX.Element[];
  onSelect: (node: NodeWithoutDescription) => void;
  selectedNodeId: string | null;
  hasCollapsibleParent?: boolean;
};

const BASE = 'SHARED_COMPONENTS.TOC_LEVEL_SELECTOR.NODE_ITEMS';

export default function NodeBranch({
  node,
  level,
  renderBranches,
  onSelect,
  selectedNodeId,
  hasCollapsibleParent,
}: Props) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<boolean>(() => level === 0);

  const title = node.id === 'general' ? t('TOC.GENERAL') : node.title;
  const avatar = node.image
    ? { src: node.image, name: t(`${BASE}.AVATAR_A11Y_LABEL`, { nodeTitle: title }), title }
    : undefined;

  const hasChildren = Boolean(node.nodes?.length);

  const collapsibleProps = hasChildren
    ? {
        onToggleCollapse: () => setCollapsed(prev => !prev),
        collapsed,
        collapseButtonLabelText: t(`${BASE}.COLLAPSE_BUTTON.A11Y_LABEL`),
        level: level as Level,
      }
    : { level: level as Level, placeholder: level === 0 };

  return (
    <>
      <List.Item
        as="radio"
        key={node.id}
        title={title}
        subText={node.prefix}
        subTextPlacement="top"
        checked={selectedNodeId === node.id}
        avatar={avatar}
        onClick={() => onSelect(node)}
        data-testid={`node-${node.id}`}
        hasCollapsibleParent={hasCollapsibleParent}
        {...collapsibleProps}
      />
      {!collapsed && hasChildren ? renderBranches(node.nodes!, level + 1, true) : undefined}
    </>
  );
}
